import { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import {
  Phone, MessageCircle, Clock, Shield, Award, Users,
  Hammer, Paintbrush, Layers, Wrench, MapPin,
  CheckCircle, Zap, ChevronRight, CalendarDays, Send
} from "lucide-react";
import { Separator } from "../components/ui/separator";

const diensten = [
  "Kleine renovaties",
  "Reparaties",
  "Timmerwerk",
  "Schilderwerk",
  "Stukadoor werk",
  "Onderhoud en afwerking",
];

const wanneerUurtarief = [
  "Kleine klussen",
  "Snelle reparaties",
  "Spoedwerk",
  "Werk zonder vaste prijs",
];

const waaromCardoso = [
  { icon: Clock, label: "8 jaar ervaring" },
  { icon: Users, label: "9 vakmannen" },
  { icon: Zap, label: "Snelle service" },
  { icon: Award, label: "Professionele afwerking" },
  { icon: Shield, label: "Betrouwbare planning" },
];

const werkgebied = ["Breukelen", "Utrecht", "Amsterdam", "Rotterdam", "Den Haag", "Hilversum", "Loosdrecht", "Amersfoort", "Almere", "Haarlem", "Leiden", "Lelystad"];

const SPOED_WA_LINK = "https://wa.me/31685071009?text=Spoedklus%20aanvraag%3A%0A%0ANaam%3A%0AAdres%3A%0ATelefoonnummer%3A%0AGewenste%20datum%3A%0ABeschrijving%20klus%3A%0A";

export default function UurtariefPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="uurtarief-page">
      <SEO
        title="Aannemer op uurtarief | Vanaf €52,50 per uur – Cardoso Bouw"
        description="Ervaren vakmannen op uurtarief vanaf €52,50/uur. Spoedklus binnen 24 uur: €75/uur. Kleine renovaties, reparaties, timmerwerk, schilderwerk. Bel 06 85 07 10 09."
        keywords="aannemer uurtarief Breukelen, vakman op uurtarief, spoedklus aannemer, klusjesman Breukelen, klusjesman Utrecht, reparatie op uurtarief, aannemer uurtarief Loosdrecht"
        breadcrumb="Werk op uurtarief"
      />

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center bg-[#005F5F]">
        <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/kkz81rty_IMG_0034.jpeg')] bg-cover bg-center opacity-15" aria-hidden="true" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-32 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#C5A059] px-4 py-2 mb-8">
              <Zap className="w-4 h-4 text-white" />
              <span className="text-white font-bold uppercase tracking-wider text-xs">Werk op uurtarief</span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white uppercase leading-tight mb-6">
              Snelle en betrouwbare vakmannen op uurtarief
            </h1>
            <div className="space-y-3 mb-10">
              <p className="text-white/90 text-lg md:text-xl font-medium">
                Vanaf <span className="text-[#C5A059] font-bold text-2xl">€52,50</span> per uur excl. btw
              </p>
              <p className="text-white/70 text-base">
                Spoedklus binnen 24 uur – <span className="text-[#C5A059] font-bold">€75</span> per uur excl. btw
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+31685071009"
                data-testid="uurtarief-hero-phone"
                className="bg-[#C5A059] hover:bg-white hover:text-[#005F5F] text-white font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 flex items-center gap-2"
              >
                <Phone className="w-4 h-4" />
                Bel direct: 06 85 07 10 09
              </a>
              <a
                href="https://wa.me/31685071009"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="uurtarief-hero-whatsapp"
                className="bg-[#25D366] hover:bg-[#1da851] text-white font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp direct
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl">
            <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Vakmannen op uurtarief</p>
            <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-bold text-[#0F172A] uppercase mb-6">
              Snel en professioneel geholpen
            </h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-4">
              Heeft u een klus die snel en professioneel uitgevoerd moet worden? Cardoso Bouw en 
              Onderhoud biedt ervaren vakmannen op uurtarief voor diverse werkzaamheden binnen en buiten.
            </p>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              Met meer dan 8 jaar ervaring en een team van 9 vakmannen staan wij klaar om u snel te helpen.
            </p>
          </div>
        </div>
      </section>

      {/* WAT WIJ DOEN */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Onze diensten</p>
          <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-bold text-[#0F172A] uppercase mb-12">
            Wat wij doen
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {diensten.map((dienst) => (
              <div key={dienst} className="bg-white p-6 border border-slate-200 flex items-center gap-4">
                <div className="w-3 h-3 bg-[#C5A059] flex-shrink-0" />
                <span className="font-medium text-slate-700">{dienst}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TARIEVEN */}
      <section className="py-20 md:py-28 bg-white" data-testid="tarieven-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Onze tarieven</p>
          <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-bold text-[#0F172A] uppercase mb-12">
            Tarieven
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Standaard */}
            <div className="border-2 border-slate-200 p-10">
              <div className="inline-flex items-center gap-2 bg-slate-100 px-4 py-2 mb-6">
                <Clock className="w-4 h-4 text-[#005F5F]" />
                <span className="text-[#005F5F] font-bold uppercase tracking-wider text-xs">Standaard</span>
              </div>
              <div className="mb-4">
                <span className="font-['Barlow_Condensed'] text-5xl font-extrabold text-[#005F5F]">€52,50</span>
                <span className="text-slate-500 text-sm ml-2">per uur excl. btw</span>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-8">
                Geschikt voor geplande werkzaamheden en kleinere klussen.
              </p>
              <a
                href="tel:+31685071009"
                data-testid="tarief-standaard-btn"
                className="flex items-center justify-center gap-2 w-full py-4 bg-[#005F5F] hover:bg-[#C5A059] text-white font-bold uppercase tracking-wider text-sm transition-all duration-300"
              >
                <Phone className="w-4 h-4" />
                Bel voor afspraak
              </a>
            </div>

            {/* Spoed */}
            <div className="border-2 border-[#C5A059] p-10 relative">
              <div className="absolute -top-4 right-6 bg-[#C5A059] px-4 py-1">
                <span className="text-white font-bold uppercase tracking-wider text-xs">Spoed</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 px-4 py-2 mb-6">
                <Zap className="w-4 h-4 text-[#C5A059]" />
                <span className="text-[#C5A059] font-bold uppercase tracking-wider text-xs">Binnen 24 uur</span>
              </div>
              <div className="mb-4">
                <span className="font-['Barlow_Condensed'] text-5xl font-extrabold text-[#C5A059]">€75</span>
                <span className="text-slate-500 text-sm ml-2">per uur excl. btw</span>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-8">
                Voor dringende werkzaamheden zorgen wij dat u binnen 24 uur geholpen wordt.
              </p>
              <a
                href={SPOED_WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tarief-spoed-btn"
                className="flex items-center justify-center gap-2 w-full py-4 bg-[#25D366] hover:bg-[#1da851] text-white font-bold uppercase tracking-wider text-sm transition-all duration-300"
              >
                <MessageCircle className="w-4 h-4" />
                Spoedklus via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SPOEDKLUS AANVRAAG */}
      <section className="py-20 md:py-28 bg-[#005F5F]" data-testid="spoed-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#C5A059] px-4 py-2 mb-6">
                <Zap className="w-4 h-4 text-white" />
                <span className="text-white font-bold uppercase tracking-wider text-xs">Spoedklus</span>
              </div>
              <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-bold text-white uppercase mb-6">
                Spoedklus? Wij helpen binnen 24 uur
              </h2>
              <p className="text-white/70 text-base leading-relaxed mb-8">
                Voor spoedklussen werken wij via WhatsApp zodat wij u snel en efficiënt kunnen helpen. 
                Stuur ons de volgende gegevens:
              </p>
              <div className="space-y-3 mb-10">
                {["Naam", "Adres", "Telefoonnummer", "Gewenste datum", "Beschrijving van de klus", "Eventueel foto's"].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <Send className="w-4 h-4 text-[#C5A059] flex-shrink-0" />
                    <span className="text-white/80 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-white/60 text-sm">
                Na ontvangst nemen wij zo snel mogelijk contact met u op en plannen wij de werkzaamheden in.
              </p>
            </div>
            <div className="text-center">
              <a
                href={SPOED_WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="spoed-wa-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1da851] text-white font-bold uppercase tracking-wider px-10 py-5 text-base transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
              >
                <MessageCircle className="w-6 h-6" />
                Spoedklus aanvragen via WhatsApp
              </a>
              <p className="text-[#C5A059] font-medium text-sm mt-6">
                Wij reageren vaak binnen 15 minuten op WhatsApp.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WANNEER UURTARIEF */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Voor wie?</p>
              <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-bold text-[#0F172A] uppercase mb-8">
                Wanneer kiezen voor uurtarief?
              </h2>
              <div className="space-y-4">
                {wanneerUurtarief.map((item) => (
                  <div key={item} className="flex items-center gap-4 p-4 bg-slate-50">
                    <CheckCircle className="w-5 h-5 text-[#005F5F] flex-shrink-0" />
                    <span className="font-medium text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Ons team</p>
              <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-bold text-[#0F172A] uppercase mb-8">
                Waarom Cardoso Bouw?
              </h2>
              <div className="space-y-4">
                {waaromCardoso.map((item) => (
                  <div key={item.label} className="flex items-center gap-4 p-4 bg-slate-50">
                    <item.icon className="w-5 h-5 text-[#C5A059] flex-shrink-0" />
                    <span className="font-medium text-slate-700">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WERKGEBIED */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">Werkgebied</p>
          <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-bold text-[#0F172A] uppercase mb-4">
            Noord- & Zuid-Holland, Utrecht, Flevoland
          </h2>
          <p className="text-slate-600 mb-8">Wij komen graag bij u langs voor een vrijblijvende offerte.</p>
          <div className="flex flex-wrap gap-4">
            {werkgebied.map((plaats) => (
              <div key={plaats} className="flex items-center gap-2 bg-white px-5 py-3 border border-slate-200">
                <MapPin className="w-4 h-4 text-[#C5A059]" />
                <span className="font-medium text-slate-700 text-sm">{plaats}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LAATSTE CTA */}
      <section className="py-20 md:py-28 bg-[#005F5F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-6">
            Direct een vakman nodig?
          </h2>
          <p className="text-white/70 text-base mb-10 max-w-lg mx-auto">
            Bel ons of stuur een WhatsApp. Wij staan voor u klaar.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a
              href="tel:+31685071009"
              data-testid="uurtarief-cta-phone"
              className="bg-[#C5A059] hover:bg-white hover:text-[#005F5F] text-white font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              06 85 07 10 09
            </a>
            <a
              href="https://wa.me/31685071009"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="uurtarief-cta-whatsapp"
              className="bg-[#25D366] hover:bg-[#1da851] text-white font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp direct
            </a>
          </div>
          <p className="text-[#C5A059] font-medium text-sm">
            Wij reageren vaak binnen 15 minuten op WhatsApp.
          </p>
        </div>
      </section>
    </div>
  );
}
